import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, ExternalLink, X, ChevronRight,
} from 'lucide-react';
import { fadeUp, staggerParent, viewportOnce } from '@/lib/motion';
import SectionHeading from '@/components/brand/SectionHeading';
import { HOME } from '@/constants/testIds';
import { resolveIcon } from '@/lib/iconMap';

const EASE = [0.22, 1, 0.36, 1];

function normalizeCert(c) {
  if (typeof c === 'string') {
    return { id: c, name: c, type: 'Certification', icon: ShieldCheck, accent: '#2F6FB0', link: '#' };
  }
  return { ...c, icon: resolveIcon(c.icon, ShieldCheck) };
}

export default function OurValues({ data, items }) {
  if (!items?.length) return null;
  const heading = data?.heading;
  const subtitle = data?.subtitle;

  const certs = (data?.certifications || []).map(normalizeCert);

  const values = items.map((v) => ({
    ...v,
    icon: resolveIcon(v.icon, ShieldCheck),
  }));

  return (
    <section id="our-values" className="py-16 bg-white overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Heading ── */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="text-center mb-14"
        >
          <SectionHeading title={heading} subtitle={subtitle} align="center" />
        </motion.div>

        {/* ── Values grid — click "View More" for popup ── */}
        <motion.div
          data-testid={HOME.valuesGrid}
          variants={staggerParent(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
        >
          {values.map((v, idx) => (
            <ValueCard key={v.title || idx} value={v} />
          ))}
        </motion.div>

        {/* ── Trust & Certifications ── */}
        <SectionHeading
          title="Trust & Certifications"
          subtitle="Internationally recognised standards and industry memberships that underpin our commitment to security, quality, and excellence."
          align="center"
          className="mb-10"
        />

        {/* ── Cert badge grid — light cards ── */}
        <motion.div
          variants={staggerParent(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5"
        >
          {certs.map((cert, idx) => (
            <CertBadge key={cert.id || idx} cert={cert} />
          ))}
        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Value Card — hover lifts card; click "View
   More" button opens the popup
───────────────────────────────────────────── */
function ValueCard({ value }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const Icon = value.icon;
  const accent = value.accent || '#2F6FB0';

  return (
    <>
      <motion.div
        variants={fadeUp}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        animate={{
          y: hovered ? -8 : 0,
          boxShadow: hovered
            ? `0 20px 44px rgba(31,45,69,0.16), 0 0 0 2px ${accent}44`
            : '0 4px 20px rgba(31,45,69,0.08)',
        }}
        style={{ boxShadow: '0 4px 20px rgba(31,45,69,0.08)' }}
        transition={{ duration: 0.35, ease: EASE }}
        className="relative rounded-2xl bg-white overflow-hidden border border-gray-100 flex flex-col"
      >
        {/* Image */}
        <div className="relative h-44 overflow-hidden flex-shrink-0">
          <motion.img
            src={value.img}
            alt={value.title}
            className="w-full h-full object-cover"
            animate={{ scale: hovered ? 1.07 : 1 }}
            style={{ scale: 1 }}
            transition={{ duration: 0.55, ease: EASE }}
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2E]/88 via-[#0F1A2E]/30 to-transparent" />

          {/* Accent bar */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-[3px]"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
            animate={{ opacity: hovered ? 1 : 0, scaleX: hovered ? 1 : 0.3 }}
            transition={{ duration: 0.3, ease: EASE }}
          />

          {/* Icon + title */}
          <div className="absolute bottom-4 left-4 flex items-center gap-2.5">
            <motion.span
              className="h-9 w-9 rounded-xl inline-flex items-center justify-center flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}99)` }}
              animate={{ scale: hovered ? 1.1 : 1 }}
              transition={{ duration: 0.3, type: 'spring', stiffness: 400, damping: 22 }}
            >
              <Icon className="h-4 w-4 text-white" />
            </motion.span>
            <h3 className="text-white font-bold text-sm leading-tight">{value.title}</h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col flex-1">
          <p className="text-sm text-slatebody leading-relaxed line-clamp-4 flex-1">{value.desc}</p>

          {/* Accent underline */}
          <motion.div
            className="mt-3 mb-4 h-[2px] rounded-full"
            style={{ background: `linear-gradient(90deg, ${accent}, transparent)`, transformOrigin: 'left' }}
            animate={{ scaleX: hovered ? 1 : 0, opacity: hovered ? 1 : 0 }}
            initial={{ scaleX: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
          />

          {/* View More button — centred, only triggers popup */}
          <div className="flex justify-center mt-auto">
            <ViewMoreButton accent={accent} onClick={() => setOpen(true)} />
          </div>
        </div>
      </motion.div>

      {/* Popup portal */}
      <AnimatePresence>
        {open && (
          <ValuePopup
            value={value}
            accent={accent}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/* ─────────────────────────────────────────────
   View More Button — pill outline with sheen,
   matches site's button language
───────────────────────────────────────────── */
function ViewMoreButton({ accent, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
      className="group relative inline-flex items-center gap-1.5 overflow-hidden rounded-full px-5 py-2 text-xs font-semibold border transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={{
        borderColor: hov ? accent : `${accent}66`,
        color: hov ? '#fff' : accent,
        background: hov ? accent : 'transparent',
        focusRingColor: accent,
      }}
      aria-label={`View more details`}
    >
      View More
      <motion.span
        animate={{ x: hov ? 3 : 0 }}
        transition={{ duration: 0.25, ease: EASE }}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </motion.span>
      {/* Sheen */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-1/2 w-full opacity-0 group-hover:opacity-100"
        style={{
          background: 'linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.25) 50%, transparent 80%)',
          animation: hov ? 'sheen 700ms ease-in-out' : 'none',
        }}
      />
    </motion.button>
  );
}

/* ─────────────────────────────────────────────
   Value Popup — artistic 3D flip-in modal
───────────────────────────────────────────── */
function ValuePopup({ value, accent, onClose }) {
  const Icon = value.icon;

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Stagger variants for content inside the modal
  const contentVariants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.18 } },
  };
  const itemVariant = {
    hidden: { opacity: 0, y: 18 },
    show:   { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
  };

  const content = (
    <motion.div
      className="fixed inset-0 z-[9000] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Animated backdrop */}
      <motion.div
        className="absolute inset-0"
        initial={{ backdropFilter: 'blur(0px)', backgroundColor: 'rgba(10,22,40,0)' }}
        animate={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(10,22,40,0.72)' }}
        exit={{ backdropFilter: 'blur(0px)', backgroundColor: 'rgba(10,22,40,0)' }}
        transition={{ duration: 0.35 }}
      />

      {/* Modal — 3D flip-up entrance */}
      <motion.div
        className="relative z-10 bg-white rounded-3xl overflow-hidden shadow-2xl w-full max-w-lg"
        initial={{ scale: 0.75, opacity: 0, y: 40, rotateX: 12 }}
        animate={{ scale: 1,    opacity: 1, y: 0,  rotateX: 0  }}
        exit={{   scale: 0.85,  opacity: 0, y: 20, rotateX: -6 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28, mass: 0.8 }}
        style={{ perspective: '1000px', transformStyle: 'preserve-3d' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero image */}
        <div className="relative h-60 overflow-hidden">
          <motion.img
            src={value.img}
            alt={value.title}
            className="w-full h-full object-cover"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.7, ease: EASE }}
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/88 via-[#0A1628]/30 to-transparent" />

          {/* Accent bar */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-[4px]"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}, ${accent}, transparent)` }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.3, duration: 0.55, ease: EASE }}
          />

          {/* Close */}
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(0,0,0,0.65)' }}
            whileTap={{ scale: 0.92 }}
            className="absolute top-3 right-3 h-8 w-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4 text-white" />
          </motion.button>

          {/* Icon + title */}
          <motion.div
            className="absolute bottom-5 left-5 flex items-center gap-3"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5, ease: EASE }}
          >
            <span
              className="h-11 w-11 rounded-xl inline-flex items-center justify-center flex-shrink-0 shadow-lg"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}bb)` }}
            >
              <Icon className="h-5 w-5 text-white" />
            </span>
            <h2 className="text-white text-xl font-bold leading-tight drop-shadow">
              {value.title}
            </h2>
          </motion.div>
        </div>

        {/* Content — staggered reveal */}
        <motion.div
          className="p-6 sm:p-7"
          variants={contentVariants}
          initial="hidden"
          animate="show"
        >
          <motion.div
            variants={itemVariant}
            className="h-[3px] w-14 rounded-full mb-5"
            style={{ background: `linear-gradient(90deg, ${accent}, ${accent}44)` }}
          />
          <motion.p variants={itemVariant} className="text-base text-slatebody leading-relaxed">
            {value.desc}
          </motion.p>
        </motion.div>
      </motion.div>
    </motion.div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
}

/* ─────────────────────────────────────────────
   Cert Badge — light white cards, coloured
   icon + hover border accent
───────────────────────────────────────────── */
function CertBadge({ cert }) {
  const [hovered, setHovered] = useState(false);
  const CertIcon = cert.icon;
  const accent = cert.accent || '#2F6FB0';
  const isLinked = cert.link && cert.link !== '#';
  const Wrapper = isLinked ? 'a' : 'div';
  const wrapperProps = isLinked
    ? { href: cert.link, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <motion.div variants={fadeUp}>
      <Wrapper
        {...wrapperProps}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="block select-none"
        style={{ cursor: isLinked ? 'pointer' : 'default' }}
      >
        <div
          className="relative rounded-2xl bg-white flex flex-col items-center justify-center text-center p-5 min-h-[148px] gap-3 border transition-all duration-350"
          style={{
            borderColor: hovered ? `${accent}55` : '#E5E7EB',
            boxShadow: hovered
              ? `0 12px 32px rgba(31,45,69,0.12), 0 0 0 1.5px ${accent}44`
              : '0 2px 12px rgba(31,45,69,0.07)',
            transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
            transition: 'all 0.38s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          {/* Coloured top border */}
          <div
            style={{
              position: 'absolute',
              top: 0, left: '12px', right: '12px',
              height: '3px',
              borderRadius: '0 0 4px 4px',
              background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
              opacity: hovered ? 1 : 0,
              transform: hovered ? 'scaleX(1)' : 'scaleX(0.2)',
              transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)',
              transformOrigin: 'center',
            }}
          />

          {/* Icon circle */}
          <div
            className="h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300"
            style={{
              backgroundColor: hovered ? `${accent}18` : `${accent}0E`,
              border: `1.5px solid ${hovered ? `${accent}55` : `${accent}22`}`,
            }}
          >
            <CertIcon
              className="h-5 w-5 transition-colors duration-300"
              style={{ color: accent }}
            />
          </div>

          {/* Name */}
          <p
            className="font-bold text-xs leading-snug transition-colors duration-300"
            style={{ color: hovered ? accent : '#1F2D45' }}
          >
            {cert.name}
          </p>

          {/* Type */}
          <p className="text-[10px] text-slatebody leading-tight">{cert.type}</p>

          {/* External arrow */}
          {isLinked && (
            <ExternalLink
              className="absolute top-3 right-3 h-3 w-3 transition-colors duration-300"
              style={{ color: hovered ? accent : '#D1D5DB' }}
            />
          )}
        </div>
      </Wrapper>
    </motion.div>
  );
}
